import { useState } from 'react';

import { deleteProperty } from '@vb-django/api/services/property';
import { usePropertyRegeneration } from '@vb-django/components/minskog/hooks/usePropertyRegeneration';
import { useUpdateProperty } from '@vb-django/components/minskog/hooks/useUpdateProperty';
import { useVisaValdaStore } from '@vb-django/components/minskog/store/VisaValdaStore';

import { usePropsDataStore } from '@store/PropsDataStore';

import { cn } from '@utils/cn';

import { Button, Chip, HelperText, Icon, Sheet, Switch, Text } from '@ui-component-library';

import { usePropertySelection } from '../../context/PropertySelectionContext';
import { removePropertyFromCookie } from '../../PropertySelector.utils';

import { PropertyShareSheet } from './PropertyShareSheet';

import type { PropertyDetailsProps } from './PropertyDetails.types';

const PropertyDetails = ({ className, isOpen, onClose, property }: PropertyDetailsProps) => {
  const { props: properties, setProps, setOneProp } = usePropsDataStore();
  const { setVisaValda } = useVisaValdaStore();
  const { updatePropertySections } = useUpdateProperty();
  const { regenerateProperty, loading: isRegenerating } = usePropertyRegeneration();
  const { pendingActivePropertyRefs, toggleProperty, setAlertMessage } = usePropertySelection();

  const [isUpdating, setIsUpdating] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const isOwner = property?.role ? property.role === 'owner' : !property?.is_shared;

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    onClose();
  };

  const handleUpdate = async () => {
    if (isUpdating || !property) {
      return;
    }

    const sections = property?.data?.sections || [];
    setIsUpdating(true);

    const { data, error } = await updatePropertySections(sections);

    if (error) {
      setAlertMessage({
        title: 'Kunde inte uppdatera fastighet',
        subtitle: 'Ett problem uppstod när försökte uppdatera fastigheten.',
        helperInfo: 'Försök igen senare eller kontakta oss för rådgivning',
        isOpen: true,
        onClose: () => setAlertMessage(null)
      });
      setIsUpdating(false);
      return;
    }

    const updatedProps = properties.map((p) => {
      const update = data.find((i: { ref: string; updated_at: string }) => i.ref === p.ref);
      if (update) {
        return {
          ...p,
          data: update.new_data,
          updated_at: update.updated_at
        };
      }
      return p;
    });

    setProps(updatedProps);
    setIsUpdating(false);
    window.location.reload();
  };

  const handleRegenerate = async () => {
    if (isRegenerating || !property) {
      return;
    }

    const result = await regenerateProperty(property, () => {
      handleClose();
    });

    if (result?.error) {
      let subtitleErrror = 'Ett problem uppstod när vi försökte återskapa fastigheten.';
      switch (result.error) {
        case 'Could not find property/user combination or regenerated to soon.':
          subtitleErrror =
            'Det gick inte att återskapa fastigheten. Vänta en stund och försök igen. Om problemet kvarstår, kontakta oss.';
          break;
        case 'Property is deprecated':
          subtitleErrror =
            'Du har valt en gammal skogsbruksplan som inte går att återskapa. Du har en ny version av denna skogsbruksplan att välja istället som går att återskapa.';
          break;
        case 'Properties based on Forestand-files cant be regenerated, they must be re-uploaded.':
          subtitleErrror = 'Forestand kan inte återskapas, de måste laddas upp igen.';
          break;
        case 'Properties can only be regenerated once a day.':
          subtitleErrror = 'Fastigheter kan bara återskapas en gång per dag.';
          break;
        default:
          subtitleErrror = 'Ett problem uppstod när vi försökte återskapa fastigheten.';
      }

      setAlertMessage({
        title: 'Kunde inte återskapa fastighet',
        subtitle: subtitleErrror,
        helperInfo: 'Försök igen senare eller kontakta oss för rådgivning',
        isOpen: true,
        onClose: () => setAlertMessage(null)
      });
    }
  };

  const handleDelete = async () => {
    if (!property) {
      return;
    }

    setAlertMessage(null);
    setOneProp({ ...property, loading: true });
    handleClose();

    const response = await deleteProperty(property.ref, property.type);

    if (response.ok) {
      removePropertyFromCookie(property.ref);
      const updatedProps = properties.filter((p) => p.ref !== property.ref);
      setProps(updatedProps);

      if (property.active) {
        setVisaValda(true);
      }
    } else {
      setOneProp({ ...property, loading: false });
      setAlertMessage({
        title: 'Kunde inte radera fastighet',
        subtitle: 'Ett fel uppstod vid radering av fastigheten permanent.',
        designation: property.designation,
        helperInfo: 'Försök igen senare eller kontakta oss för rådgivning',
        isOpen: true,
        onClose: () => setAlertMessage(null)
      });
    }
  };

  const handleConfirmDelete = () => {
    if (!property) {
      return;
    }

    setAlertMessage({
      title: 'Radera fastighet',
      subtitle: 'Är du säker på att du vill radera',
      designation: property.designation,
      isOpen: true,
      onPrimaryAction: () => handleDelete(),
      primaryActionText: 'Radera',
      onClose: () => setAlertMessage(null)
    });
  };

  const handleToggleActive = (isChecked: boolean) => {
    if (property) {
      toggleProperty(property.ref, isChecked);
    }
  };

  const handleExportPDF = () => {
    if (!property) {
      return;
    }

    window.open(`${window.location.origin}/map-api/sbp-to-pdf/?ref=${property.ref}`, '_blank');
  };

  return (
    <>
      <Sheet
        isOpen={isOpen}
        onClose={onClose}
        className={cn('flex-col', className)}
        hideBackdrop={true}
      >
        <Sheet.Header>
          <Button variant="tertiary" size="icon" onClick={handleClose}>
            <Icon name="ArrowShortSimple" className="color-charcoal-500" size="large" />
          </Button>

          <Text variant="text-lg/leading-normal/medium" className="capitalize">
            {property?.designation.toLowerCase()}
          </Text>

          <Button variant="tertiary" size="icon" onClick={handleClose}>
            <Icon name="CrossSimple" className="color-charcoal-500" size="large" />
          </Button>
        </Sheet.Header>
        <Sheet.Content className="flex flex-col gap-4 bg-[#F5F5F5]">
          <div className="rounded-lg-alt bg-white-50 gap-3-5 flex flex-col px-4 py-3">
            <div className="flex flex-row items-center justify-between">
              {property.status === 'data' ? (
                <>
                  <Text variant="text-sm/leading-normal/medium">Aktivera fastighet</Text>
                  <Switch
                    checked={property ? pendingActivePropertyRefs.has(property.ref) : false}
                    onChange={handleToggleActive}
                  />
                </>
              ) : (
                <Text
                  variant="text-sm/leading-normal/medium"
                  className="flex flex-row items-center gap-2"
                >
                  <Icon name="WarningFilled" className="fill-[#CC9000]/60" /> Fastigheten går
                  inte att visa
                </Text>
              )}
            </div>

            <div className="w-full border-b border-solid border-[#E2E8F0]"></div>

            <div className="flex flex-row items-center justify-between">
              <Text variant="text-sm/leading-normal/medium" className="text-[#737373]">
                Storlek
              </Text>
              <Text variant="text-sm/leading-normal/medium" className="text-[#737373]">
                {property?.area} ha
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Text variant="text-sm/leading-normal/medium" className="ml-4">
              Inställningar
            </Text>

            <div className="flex flex-col gap-2">
              {isOwner && property?.type === 'PROP' && (
                <>
                  <div className="bg-white-50 px-1-5 relative flex w-full flex-row items-center justify-between rounded-lg py-2">
                    <div className="flex flex-row items-center gap-3">
                      {isUpdating ? (
                        <div className="bg-cloud-200 flex items-center justify-center rounded-sm p-3">
                          <div className="h-4 w-4 animate-spin rounded-full border border-solid border-[#0F0F0F] border-t-transparent" />
                        </div>
                      ) : (
                        <Icon
                          name="ArrowsUpdate"
                          size="large"
                          className="bg-cloud-200 p-2-5 rounded-sm"
                        />
                      )}

                      <div className="flex flex-col gap-1">
                        <Text variant="text-sm/leading-normal/medium">Uppdatera</Text>

                        <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                          {property.updated_at ? property.updated_at : ''}
                        </Text>
                      </div>
                    </div>
                    <Button variant="secondary" size="xsmall" onClick={handleUpdate}>
                      {isUpdating ? 'Uppdaterar...' : 'Uppdatera'}
                    </Button>
                  </div>
                  <HelperText
                    label="Uppdaterar och ändrar endast data i avgränsningar till den senaste versionen."
                    className="mb-2 px-3 text-[#737373]"
                  />
                </>
              )}
              {isOwner && property?.type === 'PROP' && (
                <>
                  <div className="bg-white-50 px-1-5 relative flex w-full flex-row items-center justify-between rounded-lg py-2">
                    <div className="flex flex-row items-center gap-3">
                      {isRegenerating ? (
                        <div className="bg-cloud-200 flex items-center justify-center rounded-sm p-3">
                          <div className="h-4 w-4 animate-spin rounded-full border border-solid border-[#0F0F0F] border-t-transparent" />
                        </div>
                      ) : (
                        <Icon
                          name="ClockCounterClockwise"
                          size="large"
                          className="bg-cloud-200 p-2-5 rounded-sm"
                        />
                      )}

                      <div className="flex flex-col gap-1">
                        <Text variant="text-sm/leading-normal/medium">Återställ & regenerera</Text>
                        <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                          {property.updated_at ? property.updated_at : ''}
                        </Text>
                      </div>
                    </div>
                    <Button variant="secondary" size="xsmall" onClick={handleRegenerate}>
                      {isRegenerating ? 'Återställer' : 'Återställ'}
                    </Button>
                  </div>
                  <HelperText
                    label="Återställer och regenererar fastigheten, detta raderar alla ändringar."
                    className="mb-2 px-3 text-[#737373]"
                  />
                </>
              )}

              {property?.dfr && (
                <>
                  <div className="bg-white-50 px-1-5 relative flex w-full flex-row items-center justify-between rounded-lg py-2">
                    <div className="flex flex-row items-center gap-3">
                      <Icon
                        name="PdfSimple"
                        size="large"
                        className="bg-cloud-200 p-2-5 rounded-sm"
                      />

                      <div className="flex flex-col gap-1">
                        <Text variant="text-sm/leading-normal/medium">Exportera PDF</Text>
                        <Chip
                          label="Plus"
                          className="bg-[#F6F5FE] fill-[#6856C3] text-[#6856C3]"
                          icon="SparkleFilled"
                        />
                      </div>
                    </div>
                    <Button variant="secondary" size="xsmall" onClick={handleExportPDF}>
                      Exportera
                    </Button>
                  </div>
                  <HelperText
                    label="Exportera fastigheten som PDF."
                    className="mb-2 px-3 text-[#737373]"
                  />
                </>
              )}

              {isOwner && property?.type === 'PROP' && (
                <>
                  <div className="bg-white-50 px-1-5 relative flex w-full flex-row items-center justify-between rounded-lg py-2">
                    <div className="flex flex-row items-center gap-3">
                      <Icon
                        name="UserCircleSimple"
                        size="large"
                        className="bg-cloud-200 p-2-5 rounded-sm"
                      />

                      <div className="flex flex-col gap-1">
                        <Text variant="text-sm/leading-normal/medium">Dela fastighet</Text>
                        <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                          Bjud in och hantera personer
                        </Text>
                      </div>
                    </div>
                    <Button variant="secondary" size="xsmall" onClick={() => setIsShareOpen(true)}>
                      Dela
                    </Button>
                  </div>
                  <HelperText
                    label="Bjud in andra att se eller redigera fastigheten via e-post."
                    className="mb-2 px-3 text-[#737373]"
                  />
                </>
              )}

              {isOwner && (
                <Button
                  variant="tertiary"
                  fullWidth
                  onClick={handleConfirmDelete}
                  className="bg-white-50 justify-start gap-4 px-[6px] py-2"
                >
                  <Icon name="TrashBinSimple" size="large" className="fill-danger-500 p-2-5" />
                  <Text variant="text-sm/leading-normal/medium" className="text-danger-500">
                    Radera
                  </Text>
                </Button>
              )}
            </div>
          </div>
        </Sheet.Content>
      </Sheet>

      <PropertyShareSheet
        property={property}
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
      />
    </>
  );
};

export default PropertyDetails;
